import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Button } from '@/Atomic/Molecules/Button'
import { Drawer } from '@/Atomic/Templates/Drawer/Drawer'
import { DrawerBody } from '@/Atomic/Templates/Drawer/Drawer.Body'
import { DrawerHeader } from '@/Atomic/Templates/Drawer/Drawer.Header'

interface ViewLogTimeDrawerProps {
  isOpen: boolean
  onClose: () => void
  data: TimeEntryData | null
  onEdit?: () => void
  onDelete?: () => void
}

interface TimeEntryData {
  user: string
  date: string
  customer: string
  order: string
  workPackage: string
  workDuration: string
  typeOfActivity: string
  description: string
  internalRemarks: string
}

export const ViewLogTimeDrawer = ({
  isOpen,
  onClose,
  data,
  onEdit,
  onDelete,
}: ViewLogTimeDrawerProps) => {
  const { t } = useTranslation()
  const [showDeleteMenu, setShowDeleteMenu] = useState(false)

  if (!data) return null

  return (
    <Drawer isOpen={isOpen} close={onClose} direction="right">
      <DrawerHeader>
        <div className="view-log-time-drawer-header">
          <Button
            variant="icon"
            icon="cross"
            size="md"
            tooltip={t('tooltip.close')}
            onClick={onClose}
          />
          <h2 className="view-log-time-drawer-title">
            {t('timeTracking.viewDrawer.title')}
          </h2>
          <div className="view-log-time-drawer-actions">
            {onEdit && (
              <Button
                variant="icon"
                icon="edit"
                size="md"
                tooltip={t('tooltip.edit')}
                onClick={onEdit}
              />
            )}
            {onDelete && (
              <div className="view-log-time-drawer-menu">
                <Button
                  variant="icon"
                  icon="more-horizontal"
                  size="md"
                  tooltip={t('tooltip.more')}
                  onClick={() => setShowDeleteMenu(!showDeleteMenu)}
                />
                {showDeleteMenu && (
                  <div className="view-log-time-drawer-dropdown">
                    <button
                      className="view-log-time-drawer-dropdown-item"
                      onClick={() => {
                        setShowDeleteMenu(false)
                        onDelete()
                      }}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DrawerHeader>
      <DrawerBody>
        <div className="view-log-time-drawer-content">
          <div className="view-log-time-drawer-section">
            <div className="view-log-time-drawer-field">
              <label className="view-log-time-drawer-label">
                {t('timeTracking.form.user')}
              </label>
              <div className="view-log-time-drawer-value">{data.user}</div>
            </div>
            <div className="view-log-time-drawer-field">
              <label className="view-log-time-drawer-label">
                {t('timeTracking.columns.date')}
              </label>
              <div className="view-log-time-drawer-value">{data.date}</div>
            </div>
          </div>

          <div className="view-log-time-drawer-section">
            <h3 className="view-log-time-drawer-section-title">
              {t('timeTracking.viewDrawer.section.project')}
            </h3>
            <div className="view-log-time-drawer-field">
              <label className="view-log-time-drawer-label">
                {t('timeTracking.form.customer')}
              </label>
              <div className="view-log-time-drawer-value">{data.customer}</div>
            </div>
            <div className="view-log-time-drawer-field">
              <label className="view-log-time-drawer-label">
                {t('timeTracking.form.order')}
              </label>
              <div className="view-log-time-drawer-value">{data.order}</div>
            </div>
            <div className="view-log-time-drawer-field">
              <label className="view-log-time-drawer-label">
                {t('timeTracking.form.workPackage')}
              </label>
              <div className="view-log-time-drawer-value">
                {data.workPackage}
              </div>
            </div>
          </div>

          <div className="view-log-time-drawer-section">
            <h3 className="view-log-time-drawer-section-title">
              {t('timeTracking.viewDrawer.section.task')}
            </h3>
            <div className="view-log-time-drawer-field">
              <label className="view-log-time-drawer-label">
                {t('timeTracking.form.workDuration')}
              </label>
              <div className="view-log-time-drawer-value">
                {data.workDuration}
              </div>
            </div>
            <div className="view-log-time-drawer-field">
              <label className="view-log-time-drawer-label">
                {t('timeTracking.form.typeOfActivity')}
              </label>
              <div className="view-log-time-drawer-value">
                {t(`timeTracking.activityTypes.${data.typeOfActivity}`)}
              </div>
            </div>
            <div className="view-log-time-drawer-field">
              <label className="view-log-time-drawer-label">
                {t('timeTracking.form.description')}
              </label>
              <div className="view-log-time-drawer-value">
                {data.description}
              </div>
            </div>
            <div className="view-log-time-drawer-field">
              <label className="view-log-time-drawer-label">
                {t('timeTracking.form.internalRemarks')}
              </label>
              <div className="view-log-time-drawer-value">
                {data.internalRemarks}
              </div>
            </div>
          </div>
        </div>
      </DrawerBody>
    </Drawer>
  )
}
